import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useState } from 'react';
import { type Path, type PathValue, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { DatePicker } from '@/components/ui/date-picker';
import { FieldError } from '@/components/ui/field-error';
import { MonthPicker } from '@/components/ui/month-picker';
import { useCreateMmProgramJob } from '@/hooks/queries/mm-programs/use-mm-program-jobs';
import { getErrorMessage } from '@/lib/api/error';
import { type EmailMode, type PlanJobResponse, planMmProgramJob } from '@/services/api/mm-program-jobs';
import type { MmProgramSummary } from '@/services/api/mm-programs';
import { utcDayMs } from '@/utils/date';
import { formatUtcDateOnly, formatUtcMonth } from '@/utils/format';
import { pluralize } from '@/utils/string';
import { JobPlanSummary } from './job-plan';
import {
  backfillReportDays,
  backfillReportMonths,
  backfillReports,
  EMAIL_MODE_LABELS,
  EMAIL_MODES,
  emptyJobForm,
  formProblems,
  JOB_TYPE_LABELS,
  type JobFormState,
  jobFormSchema,
  lastCompletedRun,
  lastEndedDay,
  lastEndedMonth,
  monthDays,
  REQUEST_TYPES,
  type RequestType,
  RUN_HOURS,
  type RunHour,
  sendsReports,
  sendsToMarketMakers,
  takesBcc,
  toJobRequest,
  withMonth,
} from './job-request';

const jobSchema = jobFormSchema();
// bop-api needs a justification on a dry run too; the real one is asked for when queuing.
const PREVIEW_JUSTIFICATION = 'Preview';

/** "31 days · 01–31 Oct 2026" */
const describeDays = (days: string[]) => `${pluralize(days.length, 'day')} · ${formatUtcDateOnly(utcDayMs(days[0]), utcDayMs(days[days.length - 1]))}`;

/** "Oct 2026" */
const describeMonths = (months: string[]) => months.map((month) => formatUtcMonth(utcDayMs(`${month}-01`))).join(', ');

interface ReportSwitchProps {
  label: string;
  detail: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const ReportSwitch = ({ label, detail, checked, onChange }: ReportSwitchProps) => {
  const id = useId();
  return (
    <div className="flex items-start gap-3">
      <Switch checked={checked} className="mt-0.5" id={id} onCheckedChange={onChange} />
      <div className="flex flex-col">
        <Label className="font-normal" htmlFor={id}>
          {label}
        </Label>
        <span className="text-xs text-muted-foreground">{detail}</span>
      </div>
    </div>
  );
};
ReportSwitch.displayName = 'ReportSwitch';

interface RunPickerProps {
  label: string;
  day: string;
  run: RunHour;
  /** The last completed run; later days and runs cannot be picked */
  latest: { day: string; run: RunHour };
  onChange: (day: string, run: RunHour) => void;
}

const RunPicker = ({ label, day, run, latest, onChange }: RunPickerProps) => {
  const id = useId();
  const isLater = (pickedDay: string, hour: RunHour) => pickedDay === latest.day && hour > latest.run;
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {/* Moving to the latest day keeps the run when it has completed, else takes the last one that has. */}
        <DatePicker className="w-44" id={id} max={latest.day} onChange={(next) => onChange(next, isLater(next, run) ? latest.run : run)} value={day} />
        <Select onValueChange={(value) => onChange(day, value as RunHour)} value={run}>
          <SelectTrigger aria-label={`${label} run`} className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RUN_HOURS.map((hour) => (
              <SelectItem disabled={isLater(day, hour)} key={hour} value={hour}>
                {hour}:00 UTC
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
RunPicker.displayName = 'RunPicker';

interface JobFormProps {
  marketMakers: MmProgramSummary[];
}

export const JobForm = ({ marketMakers }: JobFormProps) => {
  const form = useForm<JobFormState>({ resolver: zodResolver(jobSchema), defaultValues: emptyJobForm(new Date()), mode: 'onChange' });
  const { errors } = form.formState;
  const state = form.watch();
  const [preview, setPreview] = useState<PlanJobResponse | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const createJob = useCreateMmProgramJob();
  const ids = {
    date: useId(),
    month: useId(),
    fromDay: useId(),
    mode: useId(),
    to: useId(),
    bcc: useId(),
    resend: useId(),
  };

  const now = new Date();
  const latestRun = lastCompletedRun(now);
  const problems = formProblems(state, now);
  const reportDays = backfillReportDays(state);
  const reportMonths = backfillReportMonths(state);
  const showsEmail = sendsReports(state);
  // Why Queue job is unavailable, shown on hovering it; any edit clears the preview, so problems come first.
  const queueBlockers = problems.length ? problems : !preview ? ['Preview the job first.'] : [];
  // Any edit clears the preview. Once Preview has been tried, the whole form is checked again: a problem can sit on
  // another field than the one edited, such as the last run when the first moves past it.
  const afterEdit = () => {
    setPreview(null);
    if (form.formState.isSubmitted) void form.trigger();
  };
  const setField = <N extends Path<JobFormState>>(name: N, value: PathValue<JobFormState, N>) => form.setValue(name, value, { shouldDirty: true });
  const update = (changes: Partial<JobFormState>) => {
    for (const [name, value] of Object.entries(changes)) setField(name as keyof JobFormState, value);
    afterEdit();
  };
  const toggleMaker = (p: string, checked: boolean) => update({ marketMakers: checked ? [...state.marketMakers, p] : state.marketMakers.filter((selected) => selected !== p) });
  // Inactive market makers still get their month, since their pay is owed; only active ones get daily reports.
  const monthlyOnly = state.type === 'monthly_report' || (state.type === 'score' && !backfillReports(state).includes('daily'));
  const month = state.month ? monthDays(state.month) : null;
  const makers = monthlyOnly ? marketMakers : marketMakers.filter((maker) => maker.active);

  const runPreview = form.handleSubmit(async (values) => {
    setIsPreviewing(true);
    try {
      setPreview(await planMmProgramJob(toJobRequest(values, PREVIEW_JUSTIFICATION)));
    } catch (error) {
      toast.error(`Could not plan the job: ${getErrorMessage(error)}`);
    } finally {
      setIsPreviewing(false);
    }
  });

  const queue = async () => {
    if (!preview) return;
    const values = form.getValues();
    const confirmed = await confirmAction({
      title: `Queue ${JOB_TYPE_LABELS[values.type].toLowerCase()}`,
      size: 'wide',
      actionLabel: 'Queue job',
      description: <JobPlanSummary plan={preview.plan} />,
      onConfirm: (justification) => createJob.mutateAsync(toJobRequest(values, justification)),
    });
    if (confirmed === null) return;
    toast.success('Job queued. The instance starts within a couple of minutes, or after the run in progress.');
    setPreview(null);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Job type</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-5" noValidate onSubmit={runPreview}>
          <ToggleGroup
            aria-label="Job type"
            className="w-fit"
            onValueChange={(value) => value && update({ type: value as RequestType, emailMode: '', resend: false, dailyReports: false, monthlyReports: false })}
            size="sm"
            type="single"
            value={state.type}
            variant="outline"
          >
            {REQUEST_TYPES.map((type) => (
              <ToggleGroupItem className="px-3 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" key={type} value={type}>
                {JOB_TYPE_LABELS[type]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>

          {state.type === 'score' ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap gap-6">
                <RunPicker day={state.firstDay} label="First run" latest={latestRun} onChange={(firstDay, firstRun) => update({ firstDay, firstRun })} run={state.firstRun} />
                <RunPicker day={state.lastDay} label="Last run" latest={latestRun} onChange={(lastDay, lastRun) => update({ lastDay, lastRun })} run={state.lastRun} />
              </div>
              <FieldError message={errors.lastDay?.message} />
            </div>
          ) : null}
          {reportDays.length ? (
            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium">Reports</span>
              <ReportSwitch checked={state.dailyReports} detail={describeDays(reportDays)} label="Daily reports" onChange={(dailyReports) => update({ dailyReports })} />
              {reportMonths.length ? (
                <ReportSwitch
                  checked={state.monthlyReports}
                  detail={describeMonths(reportMonths)}
                  label="Monthly report"
                  onChange={(monthlyReports) => update({ monthlyReports })}
                />
              ) : null}
            </div>
          ) : null}
          {state.type === 'daily_report' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ids.date}>Day (UTC)</Label>
              <DatePicker className="w-44" id={ids.date} max={lastEndedDay(now)} onChange={(date) => update({ date })} value={state.date} />
              <FieldError message={errors.date?.message} />
            </div>
          ) : null}
          {state.type === 'monthly_report' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={ids.month}>Month (UTC)</Label>
              <MonthPicker className="w-44" id={ids.month} max={lastEndedMonth(now)} onChange={(next) => update(withMonth(state, next))} value={state.month} />
              <FieldError message={errors.month?.message} />
              <Label className="mt-2" htmlFor={ids.fromDay}>
                Start day (UTC), optional
              </Label>
              <DatePicker
                aria-invalid={Boolean(errors.fromDay)}
                className="w-44"
                clearLabel="Report the whole month"
                disabled={!month}
                id={ids.fromDay}
                max={month?.last}
                min={month?.first}
                onChange={(fromDay) => update({ fromDay })}
                placeholder="Whole month"
                value={state.fromDay}
              />
              <span className="max-w-prose text-xs text-muted-foreground">
                Report the month from this day on. Runs before it are neither required nor reported, so their pay is not in the report. Use it only when those runs were never
                scored.
              </span>
              <FieldError message={errors.fromDay?.message} />
            </div>
          ) : null}

          {showsEmail ? (
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={ids.mode}>Email</Label>
                <Select onValueChange={(value) => update({ emailMode: value as EmailMode })} value={state.emailMode}>
                  <SelectTrigger id={ids.mode}>
                    <SelectValue placeholder="Choose what is sent" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMAIL_MODES.map((mode) => (
                      <SelectItem key={mode} value={mode}>
                        {EMAIL_MODE_LABELS[mode]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError message={errors.emailMode?.message} />
              </div>
              {state.emailMode === 'override' ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={ids.to}>Send to</Label>
                  <Input id={ids.to} placeholder="you@onetrading.com" {...form.register('emailTo', { onChange: afterEdit })} />
                  <FieldError message={errors.emailTo?.message} />
                </div>
              ) : null}
              {takesBcc(state) ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={ids.bcc}>Bcc instead (optional)</Label>
                  <Input id={ids.bcc} placeholder="ops@onetrading.com, …" {...form.register('bcc', { onChange: afterEdit })} />
                  <FieldError message={errors.bcc?.message} />
                </div>
              ) : null}
            </div>
          ) : null}

          {showsEmail ? (
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium">Reports for</legend>
              <div className="flex max-h-48 flex-col gap-1.5 overflow-y-auto">
                {makers.map((maker) => (
                  <div className="flex items-center gap-2" key={maker.p}>
                    <Checkbox checked={state.marketMakers.includes(maker.p)} id={`job-maker-${maker.p}`} onCheckedChange={(checked) => toggleMaker(maker.p, checked === true)} />
                    <Label className="font-normal" htmlFor={`job-maker-${maker.p}`}>
                      {maker.name}
                    </Label>
                  </div>
                ))}
              </div>
              {sendsToMarketMakers(state) ? (
                <div className="mt-2 flex items-center gap-2">
                  <Checkbox checked={state.resend} id={ids.resend} onCheckedChange={(checked) => update({ resend: checked === true })} />
                  <Label className="font-normal" htmlFor={ids.resend}>
                    Also send to market makers who already received a day's report
                  </Label>
                </div>
              ) : null}
            </fieldset>
          ) : null}

          {preview ? (
            <Alert>
              <AlertTitle>Plan</AlertTitle>
              <AlertDescription>
                <JobPlanSummary plan={preview.plan} />
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex gap-2">
            <Button className="gap-2" disabled={isPreviewing} type="submit" variant="outline">
              {isPreviewing ? <Spinner className="size-4" /> : null}
              Preview
            </Button>
            {queueBlockers.length ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* aria-disabled rather than disabled, so the button still takes hover and focus for the tooltip */}
                  <Button aria-disabled className="cursor-not-allowed opacity-50" type="button" variant="primary">
                    Queue job
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {queueBlockers.length === 1 ? (
                    queueBlockers[0]
                  ) : (
                    <ul className="list-disc pl-4">
                      {queueBlockers.map((blocker) => (
                        <li key={blocker}>{blocker}</li>
                      ))}
                    </ul>
                  )}
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button disabled={createJob.isPending} onClick={queue} type="button" variant="primary">
                Queue job
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
JobForm.displayName = 'JobForm';
